import FormControl from '@material-ui/core/FormControl'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import _ from 'lodash'
import styled from 'styled-components'

import {
  PRINTABLE_TYPE,
  RANKINGS_ROW_TYPE,
  STORE_AVG_TEXT,
} from 'pared/constants'
import COLORS from 'pared/constants/colors'
import { getBrandSettings } from 'pared/customer'
import DownArrow from 'pared/images/basic/arrow/caret-down.svg'
import UpArrow from 'pared/images/basic/arrow/caret-up.svg'

import PrintPdf from '../Pdf/PrintPdf'
import { getSaleTypeDisplayNames } from '../constants'
import useSalesmanshipConfig from '../hooks/useSalesmanshipConfig'
import { IOrderBy, IStaffSale } from './index'

interface IProps {
  isLoading: boolean
  salesType: string
  setSalesType: any
  staffSales: IStaffSale[]
  orderStaffSales: (
    rawStaffsales: IStaffSale[],
    totalSalesOrPpa: string,
  ) => IStaffSale[]
  isStaffSalesExpanded: boolean
  setIsStaffSalesExpanded: any
  orderBy: IOrderBy
  setOrderBy: any
  staffSalesRowData: any
  storeTitle: string
  selectedValue: string
  dayPart: string
}

interface IRankTablTh {
  textAlign?: string
}

interface IRankTableTd {
  bold?: boolean
  textAlign?: string
  isAvg?: boolean
}

const WebLargeMain = ({
  isLoading,
  salesType,
  setSalesType,
  isStaffSalesExpanded,
  setIsStaffSalesExpanded,
  orderBy,
  setOrderBy,
  staffSalesRowData,
  storeTitle,
  selectedValue,
  dayPart,
}: IProps) => {
  const salesmanshipConfig = useSalesmanshipConfig()

  if (isLoading) {
    return (
      <LoadingContainer>
        <PageStatusDiv>Loading ...</PageStatusDiv>
      </LoadingContainer>
    )
  }

  const brandSettings = getBrandSettings()
  const isQsr = brandSettings.isQsr
  const isLegacy = brandSettings.isLegacy
  const salesmanshipLabels = brandSettings.labels.salesmanship
  const saleTypeDisplayNames = getSaleTypeDisplayNames()

  const handleStaffSalesChange = async (event: any) => {
    const value = _.get(event, 'target.value', '')
    setSalesType(value)
  }

  const onToggleExpandStaffSales = () => {
    setIsStaffSalesExpanded(!isStaffSalesExpanded)
  }

  const salesOrPpaHeaderText = _.get(saleTypeDisplayNames, [salesType], '')

  const handleOrdering = (event: any) => {
    const orderingColumnValue = _.get(event, 'currentTarget.dataset.value')
    setOrderBy({
      columnName: orderingColumnValue,
      isAscending: !orderBy.isAscending,
    })
  }

  let orderStatus = null
  if (orderBy.isAscending) {
    orderStatus = <UpArrowImg src={UpArrow} alt="ASC"></UpArrowImg>
  } else {
    orderStatus = <DownArrowImg src={DownArrow} alt="DESC"></DownArrowImg>
  }

  const staffSalesRows = _.map(staffSalesRowData, (s) => {
    const rank = _.get(s, 'rank', '')
    const name = _.get(s, 'name', '')
    const roleName = _.get(s, 'roleName', '')
    const daysWorked = _.get(s, 'daysWorked', '')
    const rowType = _.get(s, 'rowType', '')

    let salesOrPpa = ''
    if (salesType === 'speed_of_service') {
      salesOrPpa = _.get(s, 'sosAvgTime', '')
    } else {
      salesOrPpa = _.get(s, 'salesOrPpa', '')
    }

    return rowType === RANKINGS_ROW_TYPE.AVG ? (
      <AvgTr>
        <RankNumberTd textAlign={'left'} bold></RankNumberTd>
        <RankTableTd textAlign={'left'} isAvg>
          {STORE_AVG_TEXT}
        </RankTableTd>
        <RankTableTd textAlign={'left'} isAvg>
          {roleName}
        </RankTableTd>
        <RankTableTd isAvg>{salesOrPpa}</RankTableTd>
        <RankTableTd isAvg>{daysWorked}</RankTableTd>
      </AvgTr>
    ) : (
      <tr>
        <RankNumberTd textAlign={'left'} bold>
          {rank}
        </RankNumberTd>
        <RankTableTd textAlign={'left'}>{name}</RankTableTd>
        <RankTableTd textAlign={'left'}>
          {salesmanshipConfig['staffSales']?.roleNameOverride || roleName}
        </RankTableTd>
        <RankTableTd>{salesOrPpa}</RankTableTd>
        <RankTableTd>{daysWorked}</RankTableTd>
      </tr>
    )
  })

  let toggledStaffSalesRows = staffSalesRows

  if (!isStaffSalesExpanded) {
    toggledStaffSalesRows = _.slice(staffSalesRows, 0, 4)
  }

  const staffSalesToggleText = isStaffSalesExpanded
    ? 'Collapse all'
    : 'Expand All'

  return (
    <>
      <StaffOuterContainer>
        <div>
          <StaffOuterHeader>
            <PageHeader>{salesmanshipLabels.staffSales}</PageHeader>

            <PrintPdf
              rankedData={staffSalesRowData}
              storeTitle={storeTitle}
              selectedValue={selectedValue}
              printableType={PRINTABLE_TYPE.STAFF_TABLE}
              orderBy={orderBy}
              salesType={salesType}
              dayPart={dayPart}
            />
          </StaffOuterHeader>

          {isQsr || !isLegacy ? null : (
            <SubtitleContainer>
              Main Dining and Bar Sales Only
            </SubtitleContainer>
          )}
        </div>

        <FilterContainer>
          <DescriptionContainer>Rank by</DescriptionContainer>
          <SelectorContainer>
            <FormControl
              variant="outlined"
              margin="dense"
              style={{ width: 220 }}
            >
              <SalesmanSelect
                displayEmpty
                value={salesType}
                onChange={handleStaffSalesChange}
              >
                <MenuItem key="gross_sales" value="gross_sales">
                  {salesmanshipLabels.totalSales}
                </MenuItem>
                <MenuItem key="app_sales" value="app_sales">
                  {salesmanshipLabels.appetizerSales}
                </MenuItem>
                <MenuItem key="lbw_sales" value="lbw_sales">
                  {salesmanshipLabels.lbwSales}
                </MenuItem>
                <MenuItem key="dessert_sales" value="dessert_sales">
                  {salesmanshipLabels.dessertSales}
                </MenuItem>
                <MenuItem key="total_ppa" value="total_ppa">
                  {salesmanshipLabels.totalPpa}
                </MenuItem>
                <MenuItem key="app_ppa" value="app_ppa">
                  {salesmanshipLabels.appetizerPpa}
                </MenuItem>
                <MenuItem key="lbw_ppa" value="lbw_ppa">
                  {salesmanshipLabels.lbwPpa}
                </MenuItem>
                <MenuItem key="dessert_ppa" value="dessert_ppa">
                  {salesmanshipLabels.dessertPpa}
                </MenuItem>
                {isQsr ? (
                  <MenuItem key="speed_of_service" value="speed_of_service">
                    {salesmanshipLabels.speedOfService}
                  </MenuItem>
                ) : null}
              </SalesmanSelect>
            </FormControl>
          </SelectorContainer>
        </FilterContainer>
      </StaffOuterContainer>
      <StaffTableContainer>
        <RankTable>
          <thead>
            <tr>
              <RankTableTh textAlign={'left'}>Rank</RankTableTh>
              <RankTableTh textAlign={'left'}>Name</RankTableTh>
              <RankTableSortableTh
                textAlign={'left'}
                onClick={handleOrdering}
                data-value={'roleName'}
              >
                <span>Position</span>
                {orderBy.columnName === 'roleName' ? orderStatus : null}
              </RankTableSortableTh>
              <RankTableSortableTh
                onClick={handleOrdering}
                data-value={'sales'}
              >
                <span>{salesOrPpaHeaderText}</span>
                {orderBy.columnName === '' || orderBy.columnName === 'sales'
                  ? orderStatus
                  : null}
              </RankTableSortableTh>
              <RankTableSortableTh
                onClick={handleOrdering}
                data-value={'daysWorked'}
              >
                <span>Shifts</span>
                {orderBy.columnName === 'daysWorked' ? orderStatus : null}
              </RankTableSortableTh>
            </tr>
          </thead>
          <tbody>{toggledStaffSalesRows}</tbody>
        </RankTable>
      </StaffTableContainer>
      <ExpandAllSpan onClick={onToggleExpandStaffSales}>
        {staffSalesToggleText}
      </ExpandAllSpan>
    </>
  )
}

const LoadingContainer = styled.div`
  padding-left: 50px;
  padding-right: 50px;
  height: 400px;
  width: 100%;
`

const AvgTr = styled.tr`
  background-color: ${COLORS.Sky} !important;
`

const PageStatusDiv = styled.div`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  color: ${COLORS.Steel};
`

const StaffOuterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0px 0px 14px;
  width: 780px;
`

const StaffOuterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`

const PageHeader = styled.div`
  font-family: Lexend-SemiBold;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  float: left;
`

const FilterContainer = styled.div`
  display: inline-block;
`

const DescriptionContainer = styled.div`
  font-family: Lexend-Regular;
  font-size: 14px;
  float: left;
  margin-top: 17px;
`

const SelectorContainer = styled.div`
  float: left;
  margin-left: 25px;
`

const SalesmanSelect = styled(Select)`
  border-radius: 0;
  background-color: white;
`

const StaffTableContainer = styled.div`
  margin-top: 30px;
  width: 800px;
`

const RankTable = styled.table`
  width: 100%;
  text-align: center;
  border-collapse: collapse;
  font-family: Lexend-Regular;

  thead tr {
    border-bottom: 1px solid ${COLORS.Chalkboard};
  }

  tbody tr:nth-child(even) {
    background-color: ${COLORS.Porcelain};
  }

  tfoot tr:first-child {
    border-top: 1px solid ${COLORS.Chalkboard};
  }

  thead th {
    font-weight: 700;
    line-height: 14px;
    padding: 10px 0;
    min-width: 50px;
  }

  tbody td {
    font-style: normal;
    font-weight: 400;
    padding-top: 18px;
    padding-bottom: 18px;
  }

  tfoot td {
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    padding: 18px 0 0 0;
  }
`

const RankTableTh = styled.th<IRankTablTh>`
  font-family: Lexend-SemiBold;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  text-align: ${(props) => (props.textAlign === 'left' ? 'left' : 'center')};
`

const UpArrowImg = styled.img`
  width: 10px;
  margin: 0 0 2px 5px;
`

const DownArrowImg = styled.img`
  width: 10px;
  margin: 0 0 -1px 5px;
`

const RankTableSortableTh = styled.th<IRankTablTh>`
  font-family: Lexend-SemiBold;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  text-align: ${(props) => (props.textAlign === 'left' ? 'left' : 'center')};
  color: ${COLORS.Plum};
  cursor: pointer;
`

const RankTableTd = styled.td<IRankTableTd>`
  color: ${(props) => (props.isAvg ? 'white' : 'black')};
  font-family: ${(props) =>
    props.bold ? 'Lexend-SemiBold' : 'Lexend-Regular'};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  text-align: ${(props) => (props.textAlign === 'left' ? 'left' : 'center')};
`

const RankNumberTd = styled(RankTableTd)`
  padding-left: 12px;
`

const ExpandAllSpan = styled.div`
  margin-top: 20px;
  font-family: Lexend-Regular;
  color: #00bfff;
  text-decoration: underline;
  cursor: pointer;
`

const SubtitleContainer = styled.div`
  position: relative;
  top: 7px;
  height: 0px;
  font-family: Lexend-Regular;
  font-style: italic;
`

export default WebLargeMain
